import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, RotateCcw, Save, Info, Clock } from "lucide-react";
import { toast } from "sonner";
import LateToleranceCard from "@/components/school/LateToleranceCard";
import CalendarRevealCard from "@/components/school/CalendarRevealCard";

const YEAR_LABEL = { 1: "I anno", 2: "II anno", 3: "III anno" };
const TRACK_LABEL = { residenziale: "Residenziale", modulare: "Modulare" };

export default function SchoolSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["cfu-targets"],
    queryFn: async () => (await api.get("/school-config/cfu-targets")).data,
  });
  const { data: leaveDefault } = useQuery({
    queryKey: ["leave-default"],
    queryFn: async () => (await api.get("/school-config/leave-default")).data,
  });
  const [drafts, setDrafts] = useState({});
  const [leaveDraft, setLeaveDraft] = useState("");
  const [applyToExisting, setApplyToExisting] = useState(false);
  const [forceOverwrite, setForceOverwrite] = useState(false);

  useEffect(() => {
    if (leaveDefault) setLeaveDraft(String(leaveDefault.default_hours ?? 0));
  }, [leaveDefault]);

  useEffect(() => {
    if (!data) return;
    const d = {};
    for (const t of data.tracks) {
      for (const y of t.years) {
        d[`${t.track_type}-${y.year_level}`] = String(y.cfu_target ?? "");
      }
      d[`${t.track_type}-total`] = String(t.total_target ?? "");
    }
    setDrafts(d);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async ({ track_type, year_level, cfu_target }) => (
      await api.put("/school-config/cfu-targets", { track_type, year_level, cfu_target })
    ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cfu-targets"] });
      qc.invalidateQueries({ queryKey: ["student-stats"] });
      qc.invalidateQueries({ queryKey: ["students-summary"] });
      toast.success("Target aggiornato");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  function saveCell(track_type, year_level) {
    const key = `${track_type}-${year_level ?? "total"}`;
    const raw = drafts[key];
    const val = raw === "" ? null : Number(raw);
    if (val !== null && Number.isNaN(val)) { toast.error("Valore non valido"); return; }
    saveMut.mutate({ track_type, year_level, cfu_target: val });
  }

  function resetCell(track_type, year_level) {
    saveMut.mutate({ track_type, year_level, cfu_target: null });
  }

  const saveLeaveMut = useMutation({
    mutationFn: async () => (await api.put("/school-config/leave-default", {
      default_hours: parseFloat(leaveDraft || "0"),
      apply_to_existing: applyToExisting,
      force_overwrite: forceOverwrite,
    })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["leave-default"] });
      qc.invalidateQueries({ queryKey: ["leave-quotas"] });
      qc.invalidateQueries({ queryKey: ["students-summary"] });
      qc.invalidateQueries({ queryKey: ["my-leave-quota"] });
      toast.success(`Monte ore default: ${d.default_hours}h${d.applied_to ? ` · applicato a ${d.applied_to} studenti` : ""}`);
      setApplyToExisting(false);
      setForceOverwrite(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (isLoading || !data) return <div className="p-10 text-center text-sm text-stone-400">Caricamento…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto">
      <PageHeader
        title="Impostazioni scuola · Target CFU"
        subtitle="Definisci i CFU richiesti per anno e per l'intero percorso. Il valore predefinito è calcolato automaticamente dal catalogo corsi; puoi impostare un override manuale se necessario."
      />

      <Card className="p-4 mb-5 border border-stone-200 rounded-xl shadow-none bg-[#FDFBF7]">
        <div className="flex items-start gap-3 text-xs text-stone-600">
          <Info size={14} className="shrink-0 mt-0.5 text-[#1C352D]"/>
          <div>
            <b>Come funziona:</b> se un campo è vuoto o non modificato, il sistema usa la somma dei CFU dei corsi assegnati a quel percorso/anno nel catalogo <b>(esclusi i seminari)</b>. Se inserisci un valore, quello prevale sul catalogo.
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
          <div className="p-5 border-b border-stone-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg grid place-items-center text-white bg-[#1C352D]">
              <Clock size={18}/>
            </div>
            <div className="flex-1">
              <div className="font-medium text-[#1C352D]">Monte ore permessi — default</div>
              <div className="text-[11px] text-stone-500">Ore assegnate automaticamente agli studenti che non hanno ancora un monte ore personalizzato.</div>
            </div>
          </div>
          <div className="p-5 flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1 max-w-xs">
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Ore annue di default</label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={leaveDraft}
                  onChange={(e) => setLeaveDraft(e.target.value)}
                  className="mt-1"
                  data-testid="leave-default-input"
                  placeholder="es. 40"
                />
              </div>
              <Button
                onClick={() => {
                  if (forceOverwrite && !window.confirm("Sovrascriverai il monte ore di TUTTI gli studenti (anche quelli con un valore personalizzato). Confermi?")) return;
                  saveLeaveMut.mutate();
                }}
                disabled={saveLeaveMut.isPending}
                className="bg-[#1C352D] hover:bg-[#264A3E]"
                data-testid="leave-default-save"
              >
                <Save size={13} className="mr-1.5"/>
                {saveLeaveMut.isPending ? "Salvataggio…" : "Salva"}
              </Button>
            </div>
            <label className="flex items-start gap-2 text-[12px] text-stone-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={applyToExisting}
                onChange={(e) => { setApplyToExisting(e.target.checked); if (e.target.checked) setForceOverwrite(false); }}
                className="w-4 h-4 accent-[#1C352D] mt-0.5"
                data-testid="leave-apply-existing"
              />
              <span>
                Applica agli studenti <b>senza</b> monte ore personalizzato
                <span className="block text-[11px] text-stone-500">Non tocca i monte ore già impostati manualmente.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-[12px] text-stone-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={forceOverwrite}
                onChange={(e) => { setForceOverwrite(e.target.checked); if (e.target.checked) setApplyToExisting(false); }}
                className="w-4 h-4 accent-red-700 mt-0.5"
                data-testid="leave-force-overwrite"
              />
              <span>
                <b className="text-red-700">Sovrascrivi</b> il monte ore di <b>tutti</b> gli studenti attivi
                <span className="block text-[11px] text-stone-500">Imposta queste ore come monte ore di tutti, sostituendo anche quelli personalizzati. Non tocca le ore già utilizzate.</span>
              </span>
            </label>
          </div>
        </Card>

        <LateToleranceCard/>

        <CalendarRevealCard/>

        {data.tracks.map((t) => (
          <Card key={t.track_type} className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
            <div className="p-5 border-b border-stone-100 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg grid place-items-center text-white ${t.track_type === "residenziale" ? "bg-emerald-700" : "bg-orange-600"}`}>
                <GraduationCap size={18}/>
              </div>
              <div>
                <div className="font-medium text-[#1C352D]">Percorso {TRACK_LABEL[t.track_type]}</div>
                <div className="text-[11px] text-stone-500">Catalogo: {t.total_from_catalog} CFU totali</div>
              </div>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
              {t.years.map((y) => {
                const key = `${t.track_type}-${y.year_level}`;
                return (
                  <div key={y.year_level}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500">{YEAR_LABEL[y.year_level]}</label>
                      {y.is_override && <Badge className="bg-amber-100 text-amber-800 text-[9px] rounded-full">Override</Badge>}
                    </div>
                    <div className="flex gap-1.5">
                      <Input
                        data-testid={`cfu-input-${t.track_type}-${y.year_level}`}
                        type="number"
                        min="0"
                        step="0.5"
                        value={drafts[key] ?? ""}
                        onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                        placeholder={String(y.from_catalog)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`cfu-save-${t.track_type}-${y.year_level}`}
                        onClick={() => saveCell(t.track_type, y.year_level)}
                        title="Salva override"
                      ><Save size={12}/></Button>
                      {y.is_override && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`cfu-reset-${t.track_type}-${y.year_level}`}
                          onClick={() => resetCell(t.track_type, y.year_level)}
                          title="Ripristina da catalogo"
                          className="text-stone-500"
                        ><RotateCcw size={12}/></Button>
                      )}
                    </div>
                    <div className="text-[10px] text-stone-500 mt-1">Catalogo: {y.from_catalog} CFU</div>
                  </div>
                );
              })}

              <div className="md:border-l md:pl-4 md:border-stone-200">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Totale diploma</label>
                  {t.total_is_override && <Badge className="bg-amber-100 text-amber-800 text-[9px] rounded-full">Override</Badge>}
                </div>
                <div className="flex gap-1.5">
                  <Input
                    data-testid={`cfu-input-${t.track_type}-total`}
                    type="number"
                    min="0"
                    step="0.5"
                    value={drafts[`${t.track_type}-total`] ?? ""}
                    onChange={(e) => setDrafts({ ...drafts, [`${t.track_type}-total`]: e.target.value })}
                    placeholder={String(t.total_from_catalog)}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`cfu-save-${t.track_type}-total`}
                    onClick={() => saveCell(t.track_type, null)}
                  ><Save size={12}/></Button>
                  {t.total_is_override && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`cfu-reset-${t.track_type}-total`}
                      onClick={() => resetCell(t.track_type, null)}
                      className="text-stone-500"
                    ><RotateCcw size={12}/></Button>
                  )}
                </div>
                <div className="text-[10px] text-stone-500 mt-1">Catalogo: {t.total_from_catalog} CFU</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
